import { getFirestore, collection, query, where, getDocs, orderBy, limit, doc, getDoc, updateDoc, increment, setDoc, deleteDoc, serverTimestamp, addDoc, runTransaction } from "firebase/firestore";
import { app } from "../firebase-config.js";
import { InAppNotificationService } from "./in-app-notification.service.js";
import { PointService } from "./point.service.js";

const db = getFirestore(app);

export const ContentService = {
    /**
     * Helper to normalize series data (stats structure)
     */
    _normalizeSeries: (doc) => {
        const data = doc.data();
        return {
            id: doc.id,
            ...data,
            stats: {
                // Robust Fallbacks for all stats
                viewCount: data.stats?.viewCount !== undefined ? data.stats.viewCount : (data.viewCount || 0),
                likeCount: data.stats?.likeCount !== undefined ? data.stats.likeCount : (data.likeCount || 0),
                commentCount: data.stats?.commentCount !== undefined ? data.stats.commentCount : (data.commentCount || 0),
                averageRating: data.stats?.averageRating || 0,
                ratingCount: data.stats?.ratingCount || 0
            }
        };
    },

    /**
     * Fetch main series list (Top 50)
     */
    getSeriesList: async (limitCount = 50) => {
        try {
            const q = query(
                collection(db, "series"),
                where("status", "==", "published"),
                orderBy("createdAt", "desc"),
                limit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(ContentService._normalizeSeries);
        } catch (error) {
            console.error("ContentService.getSeriesList Error:", error);
            throw error;
        }
    },

    /**
     * Fetch specific series by IDs (for History)
     */
    getSeriesByIds: async (ids) => {
        if (!ids || ids.length === 0) return [];

        // [FIX] Use Promise.all with individual error handling to prevent one permission error from failing the entire batch
        // This is crucial for Recommendation Service which might query IDs that are no longer published.
        const results = await Promise.all(ids.map(async (id) => {
            try {
                const d = await getDoc(doc(db, "series", id));
                if (d.exists()) return ContentService._normalizeSeries(d);
                return null;
            } catch (e) {
                // Permission Denied (e.g. unpublished) or Not Found
                // console.warn(`[ContentService] Skip restricted/missing series: ${id}`, e.code);
                return null;
            }
        }));

        return results.filter(item => item !== null);
    },

    /**
     * Fetch single series basic info (Shell) - Optimized for Library
     */
    getSeriesById: async (seriesId) => {
        try {
            const docRef = doc(db, "series", seriesId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() };
            }
            return null;
        } catch (error) {
            console.error("ContentService.getSeriesById Error:", error);
            return null;
        }
    },

    /**
     * Fetch Purchased Episodes for a specific Series
     */
    fetchPurchasedEpisodes: async (uid, seriesId) => {
        try {
            const q = query(
                collection(db, 'users', uid, 'purchased_episodes'),
                where('seriesId', '==', seriesId) // Assuming seriesId is stored
            );

            // Note: If seriesId is not indexed yet in purchased_episodes, we might need composite index.
            // But since 'purchased_episodes' typically has docId as `${seriesId}_${episodeId}`, 
            // a collection group query or just simple filtering might be needed.
            // Wait, our design was `users/{uid}/purchased_episodes/{docId}`.
            // And docId is `seriesId_episodeId`.
            // We can just fetch ALL purchased for user and filter in memory if volume is low, 
            // OR use `where('seriesId', '==', seriesId)` if we added that field.
            // Checking purchaseContent function... YES, we added `seriesId` field.
            // So this query is valid.

            const snapshot = await getDocs(q);
            const map = {};
            snapshot.forEach(doc => {
                const data = doc.data();
                map[data.episodeId] = data; // Key by EpisodeID for easy lookup
            });
            return map;
        } catch (error) {
            console.error("fetchPurchasedEpisodes Error:", error);
            return {};
        }
    },

    /**
     * Fetch single series detail
     */
    getSeriesDetail: async (seriesId) => {
        try {
            const docRef = doc(db, "series", seriesId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return ContentService._normalizeSeries(docSnap);
            }
            return null;
        } catch (error) {
            console.error("ContentService.getSeriesDetail Error:", error);
            throw error;
        }
    },

    /**
     * Fetch works by Author UID
     */
    getAuthorWorks: async (authorUid) => {
        try {
            const q = query(collection(db, 'series'),
                where('authorId', '==', authorUid),
                where('status', '==', 'published')
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(ContentService._normalizeSeries);
        } catch (error) {
            console.error("ContentService.getAuthorWorks Error:", error);
            throw error;
        }
    },

    /**
     * Fetch Episodes Subcollection
     */
    /**
     * Fetch Episodes Subcollection
     * [FIX] Removed orderBy from DB query to prevent Index/Permission conflicts.
     * Sorting is now done in-memory.
     */
    getEpisodes: async (seriesId, includeHidden = false) => {
        try {
            // [Strategic Fix] Use simple query to ensure high success rate
            // Complex queries (where + orderBy) often fail due to missing indexes or strict security rules.
            let q = collection(db, "series", seriesId, "episodes");

            if (!includeHidden) {
                q = query(q, where("status", "==", "published"));
            } else {
                // If includeHidden is true (Author), we can just fetch all.
                // We could add orderBy here, but for consistency let's sort in memory.
            }

            const snapshot = await getDocs(q);
            const episodes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

            // [FIX v1.36.204] In-Memory Sort immediately (Universal + Tie Breaker)
            return episodes.sort((a, b) => {
                // Prioritize 'order' > 'index' > 'createdAt'
                const keyA = (a.order !== undefined) ? a.order : (a.index !== undefined ? a.index : null);
                const keyB = (b.order !== undefined) ? b.order : (b.index !== undefined ? b.index : null);

                if (keyA !== null && keyB !== null) {
                    const diff = Number(keyA) - Number(keyB);
                    if (diff !== 0) return diff;
                }

                const ca = a.createdAt?.seconds || 0;
                const cb = b.createdAt?.seconds || 0;
                return ca - cb;
            });

        } catch (error) {
            console.error("ContentService.getEpisodes Error:", error);
            // [Fallback] Return empty to prevent UI crash, but log is crucial
            return [];
        }
    },

    // [Phase 4] Check Episode Ownership
    checkEpisodeUnlock: async (userId, seriesId, episodeId) => {
        if (!userId) return false;

        try {
            // 1. Check if user wrote it (Author)
            // (Optional optimization: if we have series data, but simpler to check DB or assume UI handles author access)

            // 2. Check Purchase History
            const docRef = doc(db, `users/${userId}/purchased_episodes/${seriesId}_${episodeId}`);
            const snap = await getDoc(docRef);

            if (snap.exists()) {
                const data = snap.data();
                // [RENTAL Logic] Check Expiry
                if (data.expiresAt) {
                    const now = new Date();
                    // Timestamp to Date conversion if needed (Firestore Timestamp has toDate())
                    const expiry = data.expiresAt.toDate ? data.expiresAt.toDate() : new Date(data.expiresAt);
                    if (now > expiry) {
                        console.log(`[ContentService] Episode expired at ${expiry}`);
                        return false;
                    }
                }
                return true;
            }
            return false;

        } catch (e) {
            console.warn("Unlock check failed", e);
            return false;
        }
    },

    /**
     * Increment View Count (Via Cloud Function for Analytics)
     */
    incrementViewCount: async (seriesId, authorId) => {
        try {
            // [SECURE] Use Cloud Function to ensure:
            // 1. Safe 'stats' object creation (Merge)
            // 2. Daily/Hourly Analytics logging
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);
            const logViewFn = httpsCallable(functions, 'logView');

            await logViewFn({ seriesId, authorId });
        } catch (error) {
            console.error("ContentService.incrementViewCount Error:", error);
        }
    },

    /**
     * [ALIAS] logView -> incrementViewCount (Standardization)
     */
    logView: async (seriesId, episodeId, userId) => {
        return ContentService.incrementViewCount(seriesId, userId);
    },

    /**
     * Mark Series as Completed (Analytics)
     */
    markSeriesCompleted: async (seriesId, userId) => {
        try {
            const ref = doc(db, "series", seriesId);
            await updateDoc(ref, { "stats.completions": increment(1) });

            // [TRACKING] Reward for Completion via PointService
            if (userId) {
                await PointService.earnPoints(userId, 50, 'completion', '작품 완독 보상', seriesId);
            }
        } catch (error) {
            console.error("ContentService.markSeriesCompleted Error:", error);
        }
    },

    /**
     * Check if User Liked Series
     */
    /**
     * Check if User Liked Series OR Specific Episode
     * [FIX] Supports Composite Key for Episode Granularity
     */
    checkLikeStatus: async (userId, seriesId, episodeId = null) => { // [FIX] Added episodeId
        if (!userId || !seriesId) return false;
        try {
            // [Safety Check] Ensure inputs are strings
            if (typeof userId !== 'string' || typeof seriesId !== 'string') return false;

            // [FIX] Composite Strategy
            const targetId = episodeId ? `${seriesId}_${episodeId}` : seriesId;

            const docRef = doc(db, `users/${userId}/likes`, targetId);
            const snap = await getDoc(docRef);
            return snap.exists();
        } catch (error) {
            // Suppress error to prevent UI crash, but log warning
            console.warn("[ContentService] checkLikeStatus safe-guard:", error);
            return false;
        }
    },

    /**
     * Toggle Like
     */
    /**
     * Toggle Like (Secure Server Call)
     */
    /**
     * Toggle Like (Secure Server Call)
     * [FIX] Passes episodeId for granular likes
     */
    toggleLike: async (userId, seriesId, seriesData, shouldLike, episodeId = null) => { // [FIX] Added episodeId
        try {
            // [SECURE] Call Cloud Function
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);
            const toggleLikeFn = httpsCallable(functions, 'toggleLike');

            const result = await toggleLikeFn({
                seriesId,
                episodeId, // [FIX] Pass it to backend
                title: seriesData.title,
                thumbnail: seriesData.coverImage || seriesData.thumbnail
            });

            return result.data.liked;
        } catch (error) {
            console.error("ContentService.toggleLike Error:", error);
            throw error;
        }
    },

    /**
     * Get Comments for an Episode (Server-Side Pagination)
     * Requires Composite Index: seriesId (Asc) + episodeId (Asc) + createdAt (Desc)
     */
    getComments: async (seriesId, episodeId, limitCount = 20, lastDoc = null) => {
        try {
            let q = query(
                collection(db, "comments"),
                where("seriesId", "==", String(seriesId)),
                where("episodeId", "==", String(episodeId)),
                orderBy("createdAt", "desc"),
                limit(limitCount)
            );

            if (lastDoc) {
                const { startAfter } = await import("firebase/firestore");
                q = query(q, startAfter(lastDoc));
            }

            const snapshot = await getDocs(q);
            return {
                items: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                lastVisible: snapshot.docs[snapshot.docs.length - 1]
            };
        } catch (error) {
            console.error("ContentService.getComments Error:", error);
            // If index is missing, this will throw.
            // Client should handle gracefully or Admin should create index.
            throw error;
        }
    },

    /**
     * Add Comment
     */
    addComment: async (data) => {
        try {
            await addDoc(collection(db, "comments"), {
                ...data,
                createdAt: serverTimestamp()
            });

            // Increment Comment Count on Series
            if (data.seriesId) {
                const seriesRef = doc(db, "series", data.seriesId);
                await updateDoc(seriesRef, { "stats.commentCount": increment(1) }).catch(e => console.warn("Failed to increment comment count", e));
            }

            // [TRACKING] Reward for Comment
            if (data.userId) {
                await PointService.earnPoints(data.userId, 5, 'comment', '댓글 작성 보상', data.seriesId);
            }

        } catch (error) {
            console.error("ContentService.addComment Error:", error);
            throw error;
        }
    },

    /**
     * Update Comment
     */
    updateComment: async (commentId, content) => {
        try {
            const ref = doc(db, "comments", commentId);
            await updateDoc(ref, {
                content,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("ContentService.updateComment Error:", error);
            throw error;
        }
    },

    /**
     * Delete Comment
     */
    deleteComment: async (commentId, seriesId) => {
        try {
            await deleteDoc(doc(db, "comments", commentId));

            // Decrement Comment Count on Series (Requires seriesId passed from UI)
            if (seriesId) {
                const seriesRef = doc(db, "series", seriesId);
                await updateDoc(seriesRef, { "stats.commentCount": increment(-1) }).catch(e => console.warn("Failed to decrement comment count", e));
            }
        } catch (error) {
            console.error("ContentService.deleteComment Error:", error);
            throw error;
        }
    },

    // --- Curation Methods ---

    /**
     * Get Trending Series (by Like Count)
     */
    getTrendingSeries: async (limitCount = 10) => {
        try {
            const q = query(
                collection(db, "series"),
                where("status", "==", "published"),
                orderBy("stats.likeCount", "desc"),
                limit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(ContentService._normalizeSeries);
        } catch (error) {
            console.error("ContentService.getTrendingSeries Error:", error);
            return [];
        }
    },

    /**
     * Get New Arrivals (by Date)
     */
    getNewArrivals: async (limitCount = 10) => {
        try {
            const q = query(
                collection(db, "series"),
                where("status", "==", "published"),
                orderBy("createdAt", "desc"),
                limit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(ContentService._normalizeSeries);
        } catch (error) {
            console.error("ContentService.getNewArrivals Error:", error);
            return [];
        }
    },

    /**
     * Get Series by Tag
     */
    getSeriesByTag: async (tag, limitCount = 20) => {
        try {
            const q = query(
                collection(db, "series"),
                where("status", "==", "published"),
                where("tags", "array-contains", tag),
                limit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(ContentService._normalizeSeries);
        } catch (error) {
            console.error("ContentService.getSeriesByTag Error:", error);
            return [];
        }
    },

    // --- Rating System ---

    /**
     * Rate an Episode (Hierarchical)
     */
    rateEpisode: async (userId, seriesId, episodeId, rating) => {
        try {
            // [Secure] Call Cloud Function (rateEpisode)
            // This handles Episode -> Series -> Author aggregation
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);
            const rateFn = httpsCallable(functions, 'rateEpisode');

            await rateFn({ seriesId, episodeId, rating });

            // [TRACKING] Reward logic is now on Server Side (rateEpisode)

        } catch (error) {
            console.error("ContentService.rateEpisode Error:", error);
            throw error;
        }
    },

    /**
     * check User Rating
     */
    /**
     * check User Rating
     * [FIX] Uses Composite Key to prevent ID collisions
     */
    getUserRating: async (userId, seriesId, episodeId = null) => {
        try {
            // Check specific episode rating first if provided
            if (episodeId) {
                // [FIX] Use Composite ID format: seriesId_episodeId
                const compositeId = `${seriesId}_${episodeId}`;
                const epSnap = await getDoc(doc(db, `users/${userId}/ratings`, compositeId));

                // Fallback: Check old ID format (plain index) for backward compatibility
                // but ONLY if the new composite one doesn't exist.
                if (!epSnap.exists()) {
                    const oldSnap = await getDoc(doc(db, `users/${userId}/ratings`, String(episodeId)));
                    // WARNING: The old format has collision issues, so we prioritize composite.
                    if (oldSnap.exists() && oldSnap.data().seriesId === seriesId) return oldSnap.data().rating;
                    return 0;
                }

                if (epSnap.exists()) return epSnap.data().rating;
            }

            // Fallback to Series-level rating?
            // Usually ratings are per-episode or per-series.
            // If checking series rating:
            const seriesSnap = await getDoc(doc(db, `users/${userId}/ratings`, seriesId));
            if (seriesSnap.exists()) return seriesSnap.data().rating;

            return 0;
        } catch (error) {
            console.error("ContentService.getUserRating Error:", error);
            return 0;
        }
    },

    /**
     * ADMIN: Sync/Recalculate All Series Stats (Comments, etc.)
     * Run this once to fix missing counts.
     */
    syncSeriesStats: async () => {
        // ... (Keep existing implementation if needed, or remove if migrated to triggers completely)
        // Leaving for manual admin usage
        return 0;
    },

    /**
     * [Phase 4] Get Global Economy Settings (Client-Side)
     * Used for Dynamic Pricing & Free Limits
     */
    getGlobalSettings: async () => {
        // [FIX v1.36.192] Robust Schema with Merge
        const defaultSettings = {
            default_free_limit: 3,
            rent_days: 7,
            prices: {
                // Standard Schema matching Admin Screenshot
                webnovel: { rent_price: 50, own_price: 100 },
                webtoon: { rent_price: 200, own_price: 500 },
                davinci: { rent_price: 250, own_price: 600 },
                cine_novel: { rent_price: 250, own_price: 600 }
            },
            pg_mode: 'TEST'
        };

        try {
            const docRef = doc(db, "settings", "global_economy");
            const snap = await getDoc(docRef);

            if (snap.exists()) {
                const data = snap.data();
                // Critical: Merge DB data with defaults to prevent "Partial Data" issues
                return {
                    ...defaultSettings,
                    ...data, // DB wins if key exists
                    prices: {
                        ...defaultSettings.prices,
                        ...(data.prices || {}) // Merge inner prices object
                    }
                };
            }

            // Document doesn't exist? rturn defaults
            return defaultSettings;

        } catch (error) {
            console.warn("ContentService.getGlobalSettings Error (Using Fallback):", error);
            // Network/Permission Fail -> Return Safe Defaults
            return defaultSettings;
        }
    },

    /**
     * [OCR] Fetch AI Anchors for Overlay
     * Essential for "Patented Technology" Visualization
     */
    getOCRAnchors: async (seriesId, episodeId) => {
        try {
            const q = query(collection(db, "series", seriesId, "episodes", episodeId, "ocr_anchors"));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (error) {
            console.warn("[ContentService] Failed to fetch OCR anchors (Non-critical):", error);
            return []; // Fail gracefully (Viewer continues without overlay)
        }
    },

    /**
     * [SEO] Fetch AI Metadata (Vibes, Locations)
     */
    getAIMetadata: async (seriesId) => {
        try {
            const docRef = doc(db, "series", seriesId, "ai_metadata", "seo_analysis");
            const snap = await getDoc(docRef);
            return snap.exists() ? snap.data() : null;
        } catch (error) {
            console.warn("[ContentService] Failed to fetch AI Metadata:", error);
            return null;
        }
    }
};
